"use client";

import Link from "next/link";
import type React from "react";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import { formatLongDate } from "@/lib/date/format-long-date";
import { formatRelativeTime } from "@/lib/date/format-relative-time";
import type { ProfileJobItem } from "@/lib/api/model";
import { ENROLLMENT_STATUS_META } from "@/features/enrollment/lib/status-meta";
import type { EnrollmentStatus } from "@/features/enrollment/lib/types";
import { sequenceColor } from "@/features/sequences/lib/colors";
import { pipelineHref } from "@/features/jobs/lib/pipeline-href";
import { ProjectCoverAvatar } from "@/features/projects/components/project-cover-avatar";

export function JobRow({
  item,
  candidateName,
}: {
  item: ProfileJobItem;
  candidateName?: string;
}) {
  const addedDate = formatLongDate(item.added_at);

  const sequences = item.sequences ?? [];
  const liveSequences = sequences.filter((seq) => {
    const meta = ENROLLMENT_STATUS_META[seq.status as EnrollmentStatus];
    return meta ? !meta.is_terminal : false;
  });

  return (
    <Link
      href={pipelineHref(item.job_id, candidateName)}
      className="flex w-full items-center gap-6 overflow-clip rounded-lg border bg-contrast/1 p-3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-colors hover:bg-contrast/4"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5 px-1">
          <p className="min-w-0 break-words text-sm leading-5 text-foreground/85">
            {item.job_title}
          </p>
          {item.stage_name && (
            <>
              <span className="size-1 shrink-0 rounded-full bg-contrast/25" />
              <Badge
                variant="linear"
                size="xs"
                style={
                  item.stage_color
                    ? ({
                        "--gradient-color": item.stage_color,
                      } as React.CSSProperties)
                    : undefined
                }
              >
                {item.stage_name}
              </Badge>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-[13px] leading-5 text-foreground/65">
          {item.project_title && (
            <div className="flex items-center gap-1 min-w-0">
              <ProjectCoverAvatar
                projectId={item.project_id}
                coverImageUrl={item.project_cover_image_url}
                className="size-3"
              />
              <span className="truncate">{item.project_title}</span>
            </div>
          )}
          {item.job_manager_name && (
            <div className="flex items-center gap-1 min-w-0">
              <Icon name="IconPeople" size={14} className="shrink-0" />
              <span className="truncate">{item.job_manager_name}</span>
            </div>
          )}
          {addedDate && (
            <div className="flex items-center gap-1">
              <Icon name="IconCalendar1" size={14} className="shrink-0" />
              <span>Added on {addedDate}</span>
            </div>
          )}
          {item.last_contacted_at && (
            <div className="flex items-center gap-1">
              <Icon name="IconEmail1" size={14} className="shrink-0" />
              <span>
                Last contacted {formatRelativeTime(item.last_contacted_at)}
              </span>
            </div>
          )}
        </div>

        {liveSequences.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 px-1">
            {liveSequences.map((seq) => (
              <Badge
                key={seq.sequence_id}
                variant="linear"
                size="xs"
                className="max-w-40"
                style={
                  {
                    "--gradient-color": sequenceColor(seq.sequence_id),
                  } as React.CSSProperties
                }
                title={
                  ENROLLMENT_STATUS_META[seq.status as EnrollmentStatus]
                    ? `${seq.name}: ${ENROLLMENT_STATUS_META[seq.status as EnrollmentStatus].label}`
                    : seq.name
                }
              >
                <span className="truncate">{seq.name}</span>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-center size-5 text-foreground/40">
        <Icon name="IconChevronRight" size={16} />
      </div>
    </Link>
  );
}
